import { useEffect, useState, useRef } from "react";
import { Edit3, Save, Loader2, FileText, Mail, AtSign, Info, Image as ImageIcon, ShieldCheck, HelpCircle, Megaphone, Facebook, Instagram, Twitter, Music2, Linkedin, Youtube, MessageCircle, Link2, ToggleLeft, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  {
    page: "Configuration globale",
    desc: "Adresses et emails utilisés à travers l'application.",
    fields: [
      { key: "paypal_destination_email", label: "Email PayPal destinataire (corridor France → Gabon)", icon: AtSign, type: "email",
        hint: "L'email recevra les paiements PayPal Friends & Family des expéditeurs depuis la France." },
      { key: "support_email", label: "Email support / contact client", icon: Mail, type: "email",
        hint: "Email affiché sur les pages Mentions légales, CGU et FAQ." },
    ],
  },
  {
    page: "Page d'accueil",
    desc: "Textes et images visibles sur la landing page.",
    fields: [
      { key: "hero_subtitle", label: "Sous-titre", icon: Info, type: "text", multiline: true,
        hint: "Texte affiché sous le titre principal sur la page d'accueil." },
      { key: "hero_image_url", label: "URL de l'image hero (femme avec smartphone, etc.)", icon: ImageIcon, type: "url",
        hint: "Coller un lien direct (.jpg / .png / .webp) ou utiliser le bouton de téléchargement. Laisser vide pour conserver l'image par défaut.",
        preview: true },
      { key: "fr_ga_intro", label: "Carte France → Gabon (description courte)", icon: FileText, type: "text", multiline: true,
        hint: "Texte affiché sur la carte « Échanger mes euros en FCFA »." },
      { key: "trust_title", label: "Section confiance — titre", icon: ShieldCheck, type: "text",
        hint: "Titre de la section « Sécurité & Transparence »." },
      { key: "trust_body", label: "Section confiance — texte", icon: ShieldCheck, type: "text", multiline: true,
        hint: "Description de la section confiance." },
    ],
  },
  {
    page: "FAQ",
    desc: "Page d'aide / questions fréquentes.",
    fields: [
      { key: "faq_intro", label: "Introduction de la page FAQ", icon: HelpCircle, type: "text", multiline: true },
    ],
  },
  {
    page: "Pied de page",
    desc: "Affiché en bas de toutes les pages.",
    fields: [
      { key: "footer_tagline", label: "Phrase d'accroche du footer", icon: FileText, type: "text", multiline: true },
    ],
  },
  {
    page: "Bannière promotionnelle",
    desc: "Bandeau orange affiché tout en haut du site. Idéal pour annoncer une promotion temporaire.",
    fields: [
      { key: "promo_banner_enabled", label: "Activer la bannière", icon: ToggleLeft, type: "boolean",
        hint: "Coché = la bannière est visible en haut de toutes les pages." },
      { key: "promo_banner_text", label: "Texte de la bannière", icon: Megaphone, type: "text", multiline: true,
        hint: "Ex : « Frais réduits ce mois-ci — 50 % de remise sur Gabon → France ! »" },
      { key: "promo_banner_link", label: "Lien cliquable (optionnel)", icon: Link2, type: "url",
        hint: "URL vers la page de destination (ex : /parrainage ou https://...)" },
      { key: "promo_banner_cta", label: "Libellé du lien", icon: FileText, type: "text",
        hint: "Défaut : « En savoir plus »" },
    ],
  },
  {
    page: "Réseaux sociaux (footer)",
    desc: "Liens vers vos comptes sociaux — affichés dans le pied de page. Laisser vide pour masquer une icône.",
    fields: [
      { key: "social_facebook", label: "Facebook", icon: Facebook, type: "url", hint: "https://facebook.com/..." },
      { key: "social_instagram", label: "Instagram", icon: Instagram, type: "url", hint: "https://instagram.com/..." },
      { key: "social_twitter", label: "X (Twitter)", icon: Twitter, type: "url", hint: "https://x.com/..." },
      { key: "social_tiktok", label: "TikTok", icon: Music2, type: "url", hint: "https://tiktok.com/@..." },
      { key: "social_linkedin", label: "LinkedIn", icon: Linkedin, type: "url", hint: "https://linkedin.com/company/..." },
      { key: "social_youtube", label: "YouTube", icon: Youtube, type: "url", hint: "https://youtube.com/@..." },
    ],
  },
  {
    page: "Support WhatsApp par pays",
    desc: "Numéros WhatsApp affichés dans le footer. Format international avec + (ex : +228 90000000). Laisser vide pour utiliser le numéro par défaut.",
    fields: [
      { key: "whatsapp_support_togo", label: "🇹🇬 Togo — support", icon: MessageCircle, type: "text", hint: "+228 XX XX XX XX" },
      { key: "whatsapp_support_gabon", label: "🇬🇦 Gabon — support", icon: MessageCircle, type: "text", hint: "+241 XX XX XX XX" },
      { key: "whatsapp_support_france", label: "🇫🇷 France — support", icon: MessageCircle, type: "text", hint: "+33 X XX XX XX XX" },
      { key: "whatsapp_support_rwanda", label: "🇷🇼 Rwanda — support", icon: MessageCircle, type: "text", hint: "+250 XXX XXX XXX" },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);

export default function AdminContent() {
  const { user } = useAuth();
  const [content, setContent] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  const isFullAdmin = user?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/site-content");
        setContent(data);
        setDraft(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      }
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const uploadImage = async (key, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/site-image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set(key, `${API_BASE}${data.path}`);
      toast.success("Image téléchargée — n'oubliez pas d'Enregistrer pour l'appliquer au site.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setUploading((prev) => ({ ...prev, [key]: false }));
  };

  const save = async () => {
    const payload = {};
    for (const f of ALL_FIELDS) {
      const cur = content[f.key];
      const drf = draft[f.key];
      if (f.type === "boolean") {
        if (!!cur !== !!drf) payload[f.key] = !!drf;
      } else if ((drf || "") !== (cur || "")) {
        payload[f.key] = drf;
      }
    }
    if (!Object.keys(payload).length) {
      toast.info("Aucune modification");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/site-content", payload);
      setContent(data);
      setDraft(data);
      toast.success("Contenu mis à jour");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const dirty = ALL_FIELDS.some((f) => {
    if (f.type === "boolean") return !!content[f.key] !== !!draft[f.key];
    return (draft[f.key] || "") !== (content[f.key] || "");
  });

  return (
    <div data-testid="admin-content-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Edit3 size={18} /></span>
          <div>
            <h1 className="font-heading text-2xl font-bold">Contenu du site</h1>
            <p className="text-sm text-gray-500">Modifiez les textes et adresses affichés sur le site sans intervention technique.</p>
          </div>
        </div>
        {isFullAdmin && (
          <button type="button" onClick={save} disabled={!dirty || saving} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="content-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
        )}
      </div>

      {!isFullAdmin && (
        <div className="sl-card mb-4 border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="content-viewer-banner">
          Vous êtes connecté en lecture seule. Vous pouvez consulter le contenu actuel mais pas le modifier.
        </div>
      )}

      {loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : (
        <div className="space-y-8">
          {GROUPS.map((g) => (
            <section key={g.page} className="space-y-3" data-testid={`content-group-${g.page}`}>
              <div className="border-l-4 border-[var(--sl-primary)] pl-3">
                <h2 className="font-heading text-lg font-bold">{g.page}</h2>
                <p className="text-xs text-gray-500">{g.desc}</p>
              </div>
              {g.fields.map((f) => (
                <div key={f.key} className="sl-card" data-testid={`content-field-${f.key}`}>
                  <label className="sl-label flex items-center gap-1.5">
                    <f.icon size={13} className="text-[var(--sl-primary)]" /> {f.label}
                  </label>
                  {f.type === "boolean" ? (
                    <label className="mt-1 inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={!!draft[f.key]}
                        onChange={(e) => set(f.key, e.target.checked)}
                        disabled={!isFullAdmin}
                        data-testid={`content-input-${f.key}`}
                      />
                      <span className="text-sm text-gray-700">{draft[f.key] ? "Activé" : "Désactivé"}</span>
                    </label>
                  ) : f.multiline ? (
                    <textarea
                      className="sl-input"
                      rows={3}
                      value={draft[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      disabled={!isFullAdmin}
                      maxLength={1000}
                      data-testid={`content-input-${f.key}`}
                    />
                  ) : (
                    <input
                      type={f.type}
                      className="sl-input"
                      value={draft[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)}
                      disabled={!isFullAdmin}
                      data-testid={`content-input-${f.key}`}
                    />
                  )}
                  {f.preview && isFullAdmin && (
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        ref={(el) => { fileInputRefs.current[f.key] = el; }}
                        onChange={(e) => uploadImage(f.key, e.target.files?.[0])}
                        className="hidden"
                        data-testid={`content-file-input-${f.key}`}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRefs.current[f.key]?.click()}
                        disabled={!!uploading[f.key]}
                        className="sl-btn-outline text-xs px-3 py-1.5 disabled:opacity-60"
                        data-testid={`content-upload-btn-${f.key}`}
                      >
                        {uploading[f.key] ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                        {uploading[f.key] ? "Téléchargement..." : "Télécharger depuis ma galerie"}
                      </button>
                      <span className="ml-2 text-[11px] text-gray-400">PNG, JPG ou WEBP — 8 Mo max</span>
                    </div>
                  )}
                  {f.hint && <p className="mt-1.5 text-xs text-gray-500">{f.hint}</p>}
                  {f.preview && draft[f.key] && (
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">Aperçu</p>
                      <img
                        src={draft[f.key]}
                        alt="Aperçu"
                        className="rounded-xl max-h-48 object-cover border border-gray-200"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid={`content-preview-${f.key}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
